import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import "../App.css";

const VideoPlayer = () => {
  const [searchParams] = useSearchParams();
  const fileName = searchParams.get("v");
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [downloadMessage, setDownloadMessage] = useState<string | null>(null);
  const retries = useRef(0);

  const fetchSignedUrl = async () => {
    if (!fileName) {
      setError("No video file specified.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `https://ikq1exqdje.execute-api.us-east-1.amazonaws.com/generatePresignedUrl?key=${fileName}`
      );

      if (response.status === 403 || response.status === 404) {
        setError("This video is no longer available — videos are retained for 19 days per our privacy policy.");
        setLoading(false);
        return; // definitive: do not retry
      }
      if (!response.ok) {
        throw new Error("Failed to fetch signed URL");
      }

      const data = await response.json();

      if (!data.url) {
        throw new Error("No video found");
      }

      setSignedUrl(data.url);
      setLoading(false);
    } catch (err) {
      console.error(`Attempt ${retries.current + 1}: Error fetching signed URL`, err);

      if (retries.current < 2) {
        retries.current += 1;
        setTimeout(fetchSignedUrl, 3000);
      } else {
        setError("Video not found. Please try again later.");
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const delayFetch = setTimeout(() => {
      fetchSignedUrl();
    }, 4000);

    return () => clearTimeout(delayFetch);
  }, [fileName]);

  const handlePlayClick = async () => {
    if (videoRef.current) {
      try {
        videoRef.current.muted = false; // Unmute video when played
        await videoRef.current.play();
        setIsPlaying(true);
        videoRef.current.controls = true;
      } catch (error) {
        console.error("Error playing video:", error);
      }
    }
  };

  const handleDownload = async () => {
    if (!signedUrl) return;
  
    try {
      // Show initial download message
      setDownloadMessage("Downloading started... The file will be saved to your Downloads folder.");
  
      const response = await fetch(signedUrl, { mode: "cors" });
      if (response.status === 403 || response.status === 404) {
        setDownloadMessage("This video is no longer available — videos are retained for 19 days per our privacy policy.");
        setTimeout(() => setDownloadMessage(null), 4000);
        return;
      }
      if (!response.ok) throw new Error("Failed to fetch video for download");
  
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "video.mp4";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
  
      // Update success message
      setDownloadMessage("Download complete! Check your Downloads folder.");
      setTimeout(() => setDownloadMessage(null), 4000); // Hide message after 4 seconds
    } catch (error) {
      console.error("Error downloading video:", error);
      setDownloadMessage("Failed to download video. CORS issue detected.");
      setTimeout(() => setDownloadMessage(null), 4000);
    }
  };

  return (
    <div className="video-container">
      <h2 className="video-title">Thank you for sharing <br /> your thoughts!</h2>

      <div className={signedUrl ? "video-wrapper" : ""}>
        {loading && (
          <div className="video-placeholder">
            <div className="loader"></div>
          </div>
        )}

        {!loading && signedUrl && (
          <div className="video-placeholder" onClick={handlePlayClick}>
            <video
              ref={videoRef}
              src={signedUrl}
              className={`video-thumbnail ${isPlaying ? "playing" : ""}`}
              loop={!isPlaying}
              playsInline
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              autoPlay={isPlaying}
              muted={false}
              onError={() => setError("This video is no longer available — videos are retained for 19 days per our privacy policy.")}
            />
            {!isPlaying && (
              <div className="play-button-overlay">
                <div className="play-content">
                  <svg
                    className="play-icon"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="white"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  <p className="play-text">Tap to play</p>
                </div>
              </div>
            )}
          </div>
        )}

        {error && <p className="error-message">{error}</p>}
      </div>
      
      {signedUrl && (
        <>
          <button onClick={handleDownload} className="download-button">
            <svg
              className="download-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="50"
              height="50"
              fill="white"
            >
              <path d="M21,14a1,1,0,0,0-1,1v4a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1V15a1,1,0,0,0-2,0v4a3,3,0,0,0,3,3H19a3,3,0,0,0,3-3V15A1,1,0,0,0,21,14Zm-9.71,1.71a1,1,0,0,0,.33.21.94.94,0,0,0,.76,0,1,1,0,0,0,.33-.21l4-4a1,1,0,0,0-1.42-1.42L13,12.59V3a1,1,0,0,0-2,0v9.59l-2.29-2.3a1,1,0,1,0-1.42,1.42Z"></path>
            </svg>
          </button>
          {downloadMessage && <p className="download-message">{downloadMessage}</p>}
        </>
      )}
    </div>
  );
};

export default VideoPlayer;